import { sendMessage as apiSend, escalate, warmup as apiWarmup, pollMessages } from "../api";
import {
  sessionId,
  setSessionId,
  messages,
  inputValue,
  isThinking,
  error,
  escalationView,
  escalationSent,
  customerName,
  customerEmail,
  operatorLive,
  operatorName,
  panelOpen,
} from "../store";
import { trackChatEngaged, trackEscalationLead } from "../tracking";
import { getClickIds } from "../click-ids";
import { shouldAdoptSessionId } from "../session";
import type { Config, Message } from "../types";

let cfg: Config | null = null;

export function setConfig(c: Config) {
  cfg = c;
}

let warmedUp = false;
export function warmupModal() {
  if (!cfg || warmedUp) return;
  warmedUp = true;
  apiWarmup(cfg).catch(() => {
    warmedUp = false;
  });
}

export function showGreetingIfEmpty() {
  // intentionally empty
}

const ACK_FIRST = "Hei! Takk for meldingen, skal se her... 😊";
const ACK_FOLLOWUP = "Mottatt! Sjekker det for deg 😊";

// Markerer at et menneske har tatt over og bytter widgeten til "live"-modus.
// Idempotent: legger kun til system-meldingen første gang. Brukes både her og
// (implisitt) av polling-loopen, som har sin egen tilsvarende guard.
function enterLiveMode(operator: string | null) {
  if (operator) operatorName.value = operator;
  if (!operatorLive.value) {
    operatorLive.value = true;
    messages.value = [
      ...messages.value,
      { role: "system", content: "Mattis har koblet seg på 🙌", ts: Date.now() },
    ];
  }
  startPolling();
}

export async function handleSend() {
  if (!cfg) return;
  const content = inputValue.value.trim();
  if (!content || isThinking.value) return;

  // I live-modus har et menneske tatt over → Iskra svarer ikke. Vi viser
  // derfor verken "tenker"-spinner eller det falske "Mottatt, sjekker"-ack-et.
  const live = operatorLive.value;

  const isFirstMessage = messages.value.length === 0;
  messages.value = [...messages.value, { role: "user", content, ts: Date.now() }];
  if (isFirstMessage) trackChatEngaged();
  inputValue.value = "";

  let ackMsg: Message | null = null;
  if (!live) {
    const ackText = isFirstMessage ? ACK_FIRST : ACK_FOLLOWUP;
    ackMsg = { role: "iskra", content: ackText, ts: Date.now() };
    messages.value = [...messages.value, ackMsg];
    isThinking.value = true;
  }
  error.value = null;

  try {
    const res = await apiSend(cfg, {
      sessionId: sessionId.value,
      message: content,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      referrer: document.referrer || null,
      customerName: customerName.value || null,
      customerEmail: customerEmail.value || null,
      ...getClickIds(),
    });

    // Serveren bestemmer hvilken sesjon som gjelder — også når den forkastet
    // ID-en vi sendte. Se session.ts for hvorfor dette ikke kan være
    // `if (!sessionId.value)`.
    if (shouldAdoptSessionId(sessionId.value, res.sessionId)) {
      setSessionId(res.sessionId);
    }

    const withoutAck = ackMsg
      ? messages.value.filter((m) => m !== ackMsg)
      : messages.value;

    // Serveren signaliserer at et menneske har tatt over (eller vi var allerede
    // i live-modus). Da finnes det ingen AI-respons å vise — fjern ack-et,
    // bytt til live-modus og la polling hente operatørens svar.
    if (live || res.isLive || res.intent === "live_handover") {
      messages.value = withoutAck;
      enterLiveMode(null);
      return;
    }

    messages.value = [
      ...withoutAck,
      {
        role: "iskra",
        content: res.response ?? "",
        intent: res.intent,
        calendlyLink: res.calendlyLink || undefined,
        mailDraft: res.mailDraft || undefined,
        ts: Date.now(),
      },
    ];

    if (res.intent === "static_fallback" || res.intent === "escalate") {
      startPolling();
    }
  } catch {
    error.value = "Kunne ikke nå serveren";
    const withoutAck = ackMsg
      ? messages.value.filter((m) => m !== ackMsg)
      : messages.value;
    messages.value = [
      ...withoutAck,
      {
        role: "system",
        content: 'Beklager, det oppstod en feil. Prøv "Send oss en e-post" nederst i stedet.',
        ts: Date.now(),
      },
    ];
    startPolling();
  } finally {
    isThinking.value = false;
  }
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let lastPollTs: string | null = null;
const POLL_INTERVAL = 3000;

function startPolling() {
  if (pollTimer || !cfg) return;
  pollTimer = setInterval(async () => {
    if (!cfg || !sessionId.value || !panelOpen.value) return;
    try {
      const res = await pollMessages(cfg, sessionId.value, lastPollTs);
      if (res.isLive) {
        enterLiveMode(res.operator);
      }
      for (const msg of res.messages) {
        if (msg.role === "operator") {
          const alreadyExists = messages.value.some(
            (m) => m.role === "operator" && m.content === msg.content && Math.abs(m.ts - new Date(msg.created_at).getTime()) < 5000
          );
          if (!alreadyExists) {
            messages.value = [
              ...messages.value,
              { role: "operator", content: msg.content, ts: new Date(msg.created_at).getTime() },
            ];
          }
          lastPollTs = msg.created_at;
        }
      }
    } catch {
      // Polling-feil er ikke kritisk — prøver igjen neste intervall
    }
  }, POLL_INTERVAL);
}

export function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

export async function handleEscalate(input: { name: string; email: string; message: string }) {
  if (!cfg) {
    window.location.href = buildMailto(input);
    return;
  }

  try {
    await escalate(cfg, { sessionId: sessionId.value, ...input });
    trackEscalationLead({ name: input.name, email: input.email });
    escalationView.value = false;
    escalationSent.value = true;
    startPolling();
  } catch (e) {
    window.location.href = buildMailto(input);
    throw e;
  }
}

function buildMailto(input: { name: string; email: string; message: string }): string {
  const transcript = messages.value
    .map(
      (m) =>
        `${m.role === "user" ? "Meg" : m.role === "iskra" ? "Iskra" : m.role === "operator" ? "Mattis" : "System"}: ${m.content}`
    )
    .join("\n\n");
  const body = `${input.message}\n\n---\nNavn: ${input.name}\nE-post: ${input.email}\n\nChat-historikk:\n${transcript}`;
  return `mailto:mattis@badstugutta.no?subject=${encodeURIComponent(
    "Henvendelse fra Iskra-chat"
  )}&body=${encodeURIComponent(body)}`;
}
